import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import cx from 'classnames';
import { can } from '../../../../../services';
import FlexBox from 'flexbox-react';
import { Input, Checkbox } from 'react-toolbox';
import DatePicker from 'components/DatePicker';
import BreakLine from 'components/BreakLine';
import PhoneInput from 'components/PhoneType/PhoneInput';
import StyledSelect from 'components/StyledSelect';
import ReactSelect from 'components/StyledSelect/react_select';
import PhoneType from 'components/PhoneType';
import { setEmployeeFilters } from '../../../../../shared/actionCreators/employees';
import {
  getEmployeeList,
  getEmployeeLoading,
} from '../../../../../reducers';
import styles from 'styles/form';
import { STATES, PHONE_TYPES_W_FAX, TIMEZONES, VISIBILITY_RANGES, IC_ISM_POSITIONS } from 'config/constants';
import { error } from '../../../../../utils';
import RTCheckbox from 'styles/checkbox.scss';

const STATES_SOURCE = STATES.map(state => ({ value: state, label: state }));
const TIMEZONE_SOURCE = TIMEZONES.map(tz => ({ value: tz, label: tz }));
const fullScreenInput = cx(styles.inputs, styles.fullScreenInput);
const largeInput = cx(styles.inputs, styles.largeInput);
const mediumInput = cx(styles.inputs, styles.mediumInput);
const smallInput = cx(styles.inputs, styles.smallInput);

class InstallerBasicInfoForm extends Component {
  constructor(props) {
    super(props);
    const { onChange } = this.props;
    this.onNameChange = onChange.bind(this, 'name');
    this.onStreetAddressChange = onChange.bind(this, 'street');
    this.onCityChange = onChange.bind(this, 'city');
    this.onStateChange = onChange.bind(this, 'state');
    this.onZipCodeChange = onChange.bind(this, 'zip_code');
    this.onEmailChange = onChange.bind(this, 'email');
    this.onPrimaryPhoneChange = onChange.bind(this, 'primary_phone');
    this.onPrimaryPhoneTypeChange = onChange.bind(this, 'primary_phone_type');
    this.onSecondaryPhoneChange = onChange.bind(this, 'secondary_phone');
    this.onSecondaryPhoneTypeChange = onChange.bind(this, 'secondary_phone_type');
    this.onWorkersCompChange = onChange.bind(this, 'workers_comp_expiration');
    this.onLiabilityInsuranceChange = onChange.bind(this, 'liability_insurance_expiration');
    this.onNetPayableExemptChange = onChange.bind(this, 'net_payable_exempt');
    this.onSelfSchedulableChange = onChange.bind(this, 'is_self_schedulable');
    this.onOverallPercentageChange = onChange.bind(this, 'overall_percentage');
    this.onCustomLaborPercentageChange = onChange.bind(this, 'custom_labor_percentage');
    this.onSupervisorChange = this.handleSupervisorChange.bind(this);
  }

  componentDidMount = () => {
    this.fetchEmployees();
  }

  componentDidUpdate(prevProps) {
    const prevIfiWorkroomIds = this.getIfiWorkroomIds(prevProps);
    const currentIfiWorkroomIds = this.getIfiWorkroomIds(this.props);

    // Refetch if IFI workroom IDs changed
    if (prevIfiWorkroomIds.length !== currentIfiWorkroomIds.length ||
        !prevIfiWorkroomIds.every((id, idx) => id === currentIfiWorkroomIds[idx])) {
      this.fetchEmployees();
    }
  }

  getIfiWorkroomIds = (props) => {
    const { crews = [], allWorkrooms = [] } = props;
    // Collect all unique workroom IDs from crews
    const crewWorkroomIds = [...new Set(crews.flatMap(crew =>
      (crew.workrooms || []).map(wr => (
        typeof wr === 'object' ? (wr.id || wr.value || wr) : wr
      ))))];
    // Filter to only IFI workrooms that are in crewWorkroomIds
    return allWorkrooms.filter(w => w.vendor === 'IFI' && crewWorkroomIds.includes(w.id))
      .map(w => w.id);
  }

  getSupervisorOptions = () => {
    const { employees } = this.props;
    return (employees || []).map(employee => ({
      value: employee.id,
      label: `${employee.name} - ${employee.position || 'Employee'}`,
    }));
  }

  getSupervisorValue = (options) => {
    const { installer: { assigned_supervisor, supervisor_id } } = this.props;
    const supervisorId = (assigned_supervisor && assigned_supervisor.id) || supervisor_id;
    if (supervisorId) {
      return options.find(opt => opt.value === supervisorId) || null;
    }
    return null;
  }

  fetchEmployees = () => {
    const ifiWorkroomIds = this.getIfiWorkroomIds(this.props);

    if (ifiWorkroomIds.length === 0) {
      return;
    }

    // Build filters for requestEmployeeList
    const filters = {
      is_active: true,
      for_workrooms: ifiWorkroomIds,
      for_positions: IC_ISM_POSITIONS,
    };

    // Set filters in state and request (setEmployeeFilters handles reset and request)
    this.props.setEmployeeFilters(filters);
  }

  handleSupervisorChange = (selectedOption) => {
    const supervisorId = selectedOption ? selectedOption.value : null;
    this.props.onChange('supervisor_id', supervisorId);
  }

  handleTimezone = option => this.props.onChange('timezone', option.value);

  handleVisibility = option => this.props.onChange('visibility', option.value);

  render = () => {
    const { errors, invalidInstallerFieldsMsgs, installer, role, position = '',
            mvw = {}, fieldError } = this.props;
    const address = (installer && installer.address) || {};
    const canEdit = can(role, 'update', 'installer');
    const canViewSupervisor = can(role, 'view', 'SUPERVISOR_INFO', {
      position,
      mvw,
    });
    const supervisorOptions = canViewSupervisor ? this.getSupervisorOptions() : [];
    const supervisorValue = canViewSupervisor ? this.getSupervisorValue(supervisorOptions) : null;
    return (<div>
      <FlexBox flexWrap="wrap" className={styles.divider}>
        <Input
          label="Company Name or Installer Name"
          className={fullScreenInput}
          value={installer.name}
          onChange={this.onNameChange}
          error={fieldError('name', invalidInstallerFieldsMsgs)}
          required
          disabled={!canEdit}
        />
        <BreakLine />
        <Input
          label="Street Address"
          className={largeInput}
          value={address.street}
          error={fieldError('street', invalidInstallerFieldsMsgs)}
          onChange={this.onStreetAddressChange}
          required
          disabled={!canEdit}
        />
        <Input
          label="City"
          className={mediumInput}
          value={address.city}
          error={fieldError('city', invalidInstallerFieldsMsgs)}
          onChange={this.onCityChange}
          required
          disabled={!canEdit}
        />
        <StyledSelect
          className={largeInput}
          label="State"
          onChange={this.onStateChange}
          options={STATES_SOURCE}
          value={address.state}
          placeholder={'Select State'}
          error={fieldError('state', invalidInstallerFieldsMsgs)}
          required
          disabled={!canEdit}
        />
        <Input
          label="Zip Code"
          className={smallInput}
          value={address.zip_code}
          error={fieldError('zip_code', invalidInstallerFieldsMsgs)}
          onChange={this.onZipCodeChange}
          required
          disabled={!canEdit}
        />
        <BreakLine />
        <Input
          label="Email"
          type="email"
          value={installer.email}
          className={largeInput}
          onChange={this.onEmailChange}
          error={error('user.email', errors) || fieldError('email', invalidInstallerFieldsMsgs)}
          required
        />
        <PhoneInput
          label="Primary Phone"
          type="tel"
          value={installer.primary_phone}
          className={largeInput}
          onChange={this.onPrimaryPhoneChange}
          error={fieldError('primary_phone', invalidInstallerFieldsMsgs)}
          required
        >
          <PhoneType
            source={PHONE_TYPES_W_FAX}
            value={installer.primary_phone_type}
            onChange={this.onPrimaryPhoneTypeChange}
          />
        </PhoneInput>
        <PhoneInput
          label="Secondary Phone"
          type="tel"
          value={installer.secondary_phone}
          className={largeInput}
          onChange={this.onSecondaryPhoneChange}
          error={error('user.secondary_phone', errors)}
        >
          <PhoneType
            source={PHONE_TYPES_W_FAX}
            value={installer.secondary_phone_type}
            onChange={this.onSecondaryPhoneTypeChange}
          />
        </PhoneInput>
        <ReactSelect
          className={mediumInput}
          label="Time Zone"
          placeholder={'Select Time Zone'}
          onChange={this.handleTimezone}
          options={TIMEZONE_SOURCE}
          value={installer.timezone}
          error={fieldError('timezone', invalidInstallerFieldsMsgs)}
          required
        />
      </FlexBox>
      <FlexBox flexWrap="wrap" className={styles.divider}>
        <DatePicker
          label="Worker's comp. expiration"
          className={largeInput}
          value={installer.workers_comp_expiration}
          onChange={this.onWorkersCompChange}
          error={error('workers_comp_expiration', errors)}
          disabled={!canEdit}
        />
        <DatePicker
          label="General liability insurance expiration"
          className={largeInput}
          value={installer.liability_insurance_expiration}
          onChange={this.onLiabilityInsuranceChange}
          error={error('liability_insurance_expiration', errors)}
          disabled={!canEdit}
        />
        <BreakLine />
      </FlexBox>
      <FlexBox flexWrap="wrap" className={styles.divider}>
        <Checkbox
          checked={installer.net_payable_exempt}
          label="Exempt Net Payable Reduction"
          onChange={this.onNetPayableExemptChange}
          className={styles.checkboxListItem}
          theme={RTCheckbox}
          disabled={!canEdit}
        />
        <Checkbox
          checked={installer.is_self_schedulable}
          label="Self Schedulable"
          onChange={this.onSelfSchedulableChange}
          className={styles.checkboxListItem}
          theme={RTCheckbox}
          disabled={!canEdit}
        />
        <BreakLine />
      </FlexBox>
      <FlexBox flexWrap="wrap" className={styles.divider}>
        <ReactSelect
          className={mediumInput}
          label="Schedule Visibility"
          placeholder={'Select Range'}
          onChange={this.handleVisibility}
          options={VISIBILITY_RANGES}
          value={installer.visibility}
          error={fieldError('visibility', invalidInstallerFieldsMsgs)}
          required
        />
        <BreakLine />
      </FlexBox>
      {canViewSupervisor && (
      // "Supervisor" is labeled as "Coordinator" in the UI since
      // that's more familiar to IFI users, but the value is still
      // stored as "supervisor_id" in the backend.
        <FlexBox flexWrap="wrap" className={styles.divider}>
          <ReactSelect
            className={fullScreenInput}
            label="Coordinator"
            placeholder="Select Coordinator"
            options={supervisorOptions}
            value={supervisorValue}
            onChange={this.onSupervisorChange}
            clearable
            isLoading={this.props.employeeLoading}
            disabled={!canEdit}
          />
          <BreakLine />
        </FlexBox>
      )}
      {mvw.ifi && (
        <FlexBox flexWrap="wrap" className={styles.divider}>
          <Input
            label="Overall % (IFI)"
            type="number"
            className={mediumInput}
            value={installer.overall_percentage != null ? String(installer.overall_percentage) : ''}
            onChange={this.onOverallPercentageChange}
            disabled={!canEdit}
          />
          <Input
            label="Custom Labor % (IFI)"
            type="number"
            className={mediumInput}
            value={installer.custom_labor_percentage != null ? String(installer.custom_labor_percentage) : ''}
            onChange={this.onCustomLaborPercentageChange}
            disabled={!canEdit}
          />
          <BreakLine />
        </FlexBox>
      )}
    </div>);
  }
}

InstallerBasicInfoForm.propTypes = {
  onChange: PropTypes.func,
  errors: PropTypes.object,
  invalidInstallerFieldsMsgs: PropTypes.array,
  installer: PropTypes.object,
  crews: PropTypes.array,
  allWorkrooms: PropTypes.array,
  employees: PropTypes.array,
  employeeLoading: PropTypes.bool,
  setEmployeeFilters: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
  position: PropTypes.string,
  mvw: PropTypes.object,
  fieldError: PropTypes.func,
};

const getEmployeeOptions = createSelector(
  getEmployeeList,
  employees => employees || [],
);

const mapStateToProps = state => ({
  employees: getEmployeeOptions(state),
  employeeLoading: getEmployeeLoading(state),
});

const mapDispatchToProps = dispatch => ({
  setEmployeeFilters: filters => dispatch(setEmployeeFilters(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InstallerBasicInfoForm);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/installers/components/installerBasicInfoForm.jsx